import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { API_BASE_URL } from "@/lib/apiBase";
import { toastError } from "@/lib/toast";
import { useSocialPaths } from "@/lib/socialPaths";
import { useSocialWriteApis } from "@/lib/socialWriteApis";
import SocialUserAvatar from "@/components/avatars/SocialUserAvatar";
import OrganizationAvatar from "@/components/avatars/OrganizationAvatar";

const PAGE_SIZE = 50;
const UNFOLLOW_ARM_MS = 3000;

/**
 * Own-profile Followers / Following list dialog.
 * Includes people and org Circa Social profiles.
 * Following rows: two-stage unfollow (Following → Unfollow) with no confirm modal.
 */
const SocialFollowListDialog = ({
  open,
  onOpenChange,
  mode, // "followers" | "following"
  handle,
  accessToken,
  onFollowingCountDelta,
}) => {
  const navigate = useNavigate();
  const paths = useSocialPaths();
  const writeApis = useSocialWriteApis();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [busyHandle, setBusyHandle] = useState(null);
  const [armedHandle, setArmedHandle] = useState(null);
  const armTimerRef = useRef(null);

  const clearArm = useCallback(() => {
    if (armTimerRef.current) {
      clearTimeout(armTimerRef.current);
      armTimerRef.current = null;
    }
    setArmedHandle(null);
  }, []);

  const armUnfollow = useCallback(
    (username) => {
      clearArm();
      setArmedHandle(username);
      armTimerRef.current = setTimeout(() => {
        setArmedHandle(null);
        armTimerRef.current = null;
      }, UNFOLLOW_ARM_MS);
    },
    [clearArm]
  );

  useEffect(() => {
    if (!open) {
      clearArm();
      setUsers([]);
      setHasMore(false);
    }
  }, [open, clearArm]);

  useEffect(() => () => clearArm(), [clearArm]);

  const load = useCallback(
    async (offset = 0, append = false) => {
      if (!accessToken || !handle || !mode) return;
      setLoading(true);
      try {
        const path =
          mode === "followers"
            ? `${API_BASE_URL}/social/users/${encodeURIComponent(handle)}/followers`
            : `${API_BASE_URL}/social/users/${encodeURIComponent(handle)}/following`;
        const res = await axios.get(path, {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: { limit: PAGE_SIZE, offset },
        });
        const next = res.data?.users || [];
        setUsers((prev) => (append ? [...prev, ...next] : next));
        setHasMore(Boolean(res.data?.has_more));
      } catch (err) {
        toastError(
          err.response?.data?.message || err.message || "Could not load list"
        );
        if (!append) setUsers([]);
      } finally {
        setLoading(false);
      }
    },
    [accessToken, handle, mode]
  );

  useEffect(() => {
    if (!open || !handle) return;
    load(0, false);
  }, [open, handle, mode, load]);

  const goToProfile = (entry) => {
    if (!entry?.username) return;
    onOpenChange(false);
    if (entry.is_org) {
      navigate(paths.org(entry.username));
    } else {
      navigate(paths.user(entry.username));
    }
  };

  const followEntry = async (entry) => {
    const username = entry?.username;
    if (!username || busyHandle) return;
    if (writeApis.isActAsOrg && !writeApis.allowEngage) return;
    setBusyHandle(username);
    try {
      const url = entry.is_org
        ? writeApis.followOrgUrl(username)
        : writeApis.followUserUrl(username);
      await axios.post(
        url,
        {},
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      setUsers((prev) =>
        prev.map((u) =>
          u.username === username ? { ...u, is_following: true } : u
        )
      );
      onFollowingCountDelta?.(1);
    } catch (err) {
      toastError(
        err.response?.data?.message || err.message || "Could not follow"
      );
    } finally {
      setBusyHandle(null);
    }
  };

  const unfollowEntry = async (entry) => {
    const username = entry?.username;
    if (!username || busyHandle) return;
    if (writeApis.isActAsOrg && !writeApis.allowEngage) return;
    setBusyHandle(username);
    clearArm();
    try {
      const url = entry.is_org
        ? writeApis.followOrgUrl(username)
        : writeApis.followUserUrl(username);
      await axios.delete(url, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (mode === "following") {
        setUsers((prev) => prev.filter((u) => u.username !== username));
      } else {
        setUsers((prev) =>
          prev.map((u) =>
            u.username === username ? { ...u, is_following: false } : u
          )
        );
      }
      onFollowingCountDelta?.(-1);
    } catch (err) {
      toastError(
        err.response?.data?.message || err.message || "Could not unfollow"
      );
    } finally {
      setBusyHandle(null);
    }
  };

  const onFollowingButtonClick = (e, entry) => {
    e.stopPropagation();
    e.preventDefault();
    if (busyHandle) return;
    if (armedHandle === entry.username) {
      unfollowEntry(entry);
      return;
    }
    armUnfollow(entry.username);
  };

  const title = mode === "followers" ? "Followers" : "Following";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-md">
        <DialogHeader className="border-b border-border px-4 py-3">
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
          {loading && users.length === 0 ? (
            <div className="flex justify-center py-10">
              <div className="h-7 w-7 animate-spin rounded-full border-b-2 border-primary" />
            </div>
          ) : users.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">
              {mode === "followers"
                ? "No followers yet."
                : "Not following anyone yet."}
            </p>
          ) : (
            <ul className="space-y-1">
              {users.map((u) => {
                const isArmed = armedHandle === u.username;
                const rowBusy = busyHandle === u.username;
                const rowKey = u.is_org ? `org:${u.username}` : u.username;

                return (
                  <li key={rowKey}>
                    <div className="flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-muted/50">
                      <button
                        type="button"
                        className="flex min-w-0 flex-1 items-center gap-3 text-left"
                        onClick={() => goToProfile(u)}
                      >
                        {u.is_org ? (
                          <OrganizationAvatar
                            org={{
                              name: u.display_name,
                              avatar_url: u.avatar_url,
                            }}
                            className="h-10 w-10 shrink-0"
                          />
                        ) : (
                          <SocialUserAvatar
                            user={u}
                            className="h-10 w-10 shrink-0"
                          />
                        )}
                        <div className="min-w-0">
                          <p className="truncate font-medium leading-tight">
                            {u.display_name}
                          </p>
                          <p className="truncate text-xs text-muted-foreground">
                            @{u.username}
                            {u.is_org ? " · Organization" : ""}
                          </p>
                        </div>
                      </button>

                      {u.is_self ? null : mode === "following" ||
                        u.is_following ? (
                        <Button
                          type="button"
                          size="sm"
                          variant={isArmed ? "destructive" : "outline"}
                          disabled={rowBusy || Boolean(busyHandle && !rowBusy)}
                          onClick={(e) => onFollowingButtonClick(e, u)}
                        >
                          {rowBusy ? "…" : isArmed ? "Unfollow" : "Following"}
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          disabled={rowBusy || Boolean(busyHandle && !rowBusy)}
                          onClick={(e) => {
                            e.stopPropagation();
                            followEntry(u);
                          }}
                        >
                          {rowBusy ? "…" : "Follow"}
                        </Button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {hasMore ? (
            <div className="px-2 pb-3 pt-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="w-full"
                disabled={loading}
                onClick={() => load(users.length, true)}
              >
                {loading ? "Loading…" : "Load more"}
              </Button>
            </div>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SocialFollowListDialog;
